import type { WorkerMessage, WorkerRequest, WorkerResponse, EmulatorState } from './messages';
import type { ModelConfig } from '../../model/ModelConfig';
import type { ChipsetSnapshot } from '../chipset/Chipset';
import type { RegisterBank } from '../cpu/registers';
import type { DisassembledInstruction } from '../cpu/disassembler';
import type { InputEvent } from '../../input/InputProvider';
import type { StorageProvider } from '../../storage/StorageProvider';
import { serializeSnapshot, deserializeSnapshot, isSerializedSnapshot, collectTransferables } from './snapshot-serializer';

interface BreakpointData {
  address: number;
  csr: number;
  condition?: string;
}

interface WorkerLike {
  onmessage: ((e: MessageEvent<WorkerMessage>) => void) | null;
  onerror: ((e: ErrorEvent) => void) | null;
  postMessage(message: unknown, transfer?: Transferable[]): void;
  terminate(): void;
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (reason: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

export class WorkerBridge {
  private worker: WorkerLike | null = null;
  private pendingRequests: Map<string, PendingRequest> = new Map();
  private requestTimeout: number = 5000;
  private latestState: EmulatorState | null = null;
  private workerFactory: (() => WorkerLike) | null = null;
  private _modelConfig: ModelConfig | null = null;
  private storageProvider: StorageProvider | null = null;
  private _mode: 'worker' | 'inline' = 'worker';

  onStateUpdate: ((state: EmulatorState) => void) | null = null;
  onBreakpointHit: ((bp: unknown) => void) | null = null;
  onError: ((error: Error) => void) | null = null;

  constructor(workerOrUrl: WorkerLike | string) {
    if (typeof workerOrUrl === 'string') {
      const url = workerOrUrl;
      this.workerFactory = () => new Worker(url);
      this.worker = this.workerFactory();
      this._mode = 'worker';
    } else {
      this.worker = workerOrUrl;
      this._mode = 'inline';
    }
    this.worker.onmessage = (e: MessageEvent<WorkerMessage>) => this.onMessage(e);
    this.worker.onerror = (e: ErrorEvent) => {
      if (this.onError) {
        this.onError(new Error(e.message || 'Worker error'));
      }
    };
  }

  async init(modelConfig: ModelConfig): Promise<void> {
    this._modelConfig = modelConfig;
    await this.sendRequest('init', [modelConfig]);
  }

  setStorageProvider(storage: StorageProvider): void {
    this.storageProvider = storage;
  }

  private onMessage(e: MessageEvent<WorkerMessage>): void {
    const msg = e.data;
    if (msg.type === 'response') {
      const pending = this.pendingRequests.get(msg.id);
      if (pending) {
        clearTimeout(pending.timer);
        this.pendingRequests.delete(msg.id);
        if (msg.success) {
          let result = msg.result;
          if (isSerializedSnapshot(result)) {
            result = deserializeSnapshot(result);
          }
          pending.resolve(result);
        } else {
          pending.reject(new Error(msg.error || 'Unknown error'));
        }
      }
    } else if (msg.type === 'request') {
      this.handleHostRequest(msg);
    } else if (msg.type === 'event') {
      this.handleEvent(msg.event, msg.data);
    }
  }

  private handleHostRequest(msg: WorkerRequest): void {
    const storage = this.storageProvider;

    if (msg.method.startsWith('host.storage.')) {
      this.processStorageRequest(msg, storage);
    } else {
      const response: WorkerResponse = {
        type: 'response',
        id: msg.id,
        success: false,
        error: `Unknown host method: ${msg.method}`,
        timestamp: Date.now(),
      };
      this.worker?.postMessage(response);
    }
  }

  private processStorageRequest(msg: WorkerRequest, storage: StorageProvider | null): void {
    if (!storage) {
      const response: WorkerResponse = {
        type: 'response',
        id: msg.id,
        success: false,
        error: 'Storage provider not set',
        timestamp: Date.now(),
      };
      this.worker?.postMessage(response);
      return;
    }

    const method = msg.method.slice('host.storage.'.length);

    const respond = (result: unknown) => {
      const response: WorkerResponse = {
        type: 'response',
        id: msg.id,
        success: true,
        result,
        timestamp: Date.now(),
      };
      this.worker?.postMessage(response);
    };

    const respondError = (error: string) => {
      const response: WorkerResponse = {
        type: 'response',
        id: msg.id,
        success: false,
        error,
        timestamp: Date.now(),
      };
      this.worker?.postMessage(response);
    };

    switch (method) {
      case 'get':
        storage.get(msg.params[0] as string).then(data => {
          if (data === null) {
            respond(null);
          } else {
            respond({ data: Array.from(data) });
          }
        }).catch(err => respondError(err.message));
        break;
      case 'set':
        storage.set(msg.params[0] as string, new Uint8Array(msg.params[1] as number[]))
          .then(() => respond(undefined))
          .catch(err => respondError(err.message));
        break;
      case 'delete':
        storage.delete(msg.params[0] as string)
          .then(() => respond(undefined))
          .catch(err => respondError(err.message));
        break;
      case 'list':
        storage.list(msg.params[0] as string)
          .then(keys => respond(keys))
          .catch(err => respondError(err.message));
        break;
      case 'isAvailable':
        respond(storage.isAvailable());
        break;
      case 'getStorageType':
        respond(storage.getStorageType());
        break;
      default:
        respondError(`Unknown storage method: ${method}`);
        break;
    }
  }

  private handleEvent(event: string, data: unknown): void {
    switch (event) {
      case 'stateUpdate':
        this.latestState = data as EmulatorState;
        if (this.onStateUpdate) {
          this.onStateUpdate(this.latestState);
        }
        break;
      case 'breakpointHit':
        if (this.onBreakpointHit) {
          this.onBreakpointHit(data);
        }
        break;
      case 'error':
        if (this.onError) {
          this.onError(data instanceof Error ? data : new Error(String(data)));
        }
        break;
    }
  }

  sendRequest(method: string, params: unknown[], transferables?: Transferable[]): Promise<unknown> {
    return new Promise((resolve, reject) => {
      if (!this.worker) {
        reject(new Error('Worker is not initialized'));
        return;
      }

      const id = generateId();
      const request: WorkerRequest = {
        type: 'request',
        id,
        method,
        params,
        timestamp: Date.now(),
      };

      const timer = setTimeout(() => {
        this.pendingRequests.delete(id);
        reject(new Error(`Request '${method}' timed out after ${this.requestTimeout}ms`));
      }, this.requestTimeout);

      this.pendingRequests.set(id, { resolve, reject, timer });
      this.worker.postMessage(request, transferables ?? []);
    });
  }

  async callMethod(method: string, ...params: unknown[]): Promise<unknown> {
    return this.sendRequest(method, params);
  }

  async start(): Promise<void> {
    await this.sendRequest('chipset.start', []);
  }

  async pause(): Promise<void> {
    await this.sendRequest('chipset.pause', []);
  }

  async step(): Promise<void> {
    await this.sendRequest('chipset.step', []);
  }

  async reset(): Promise<void> {
    await this.sendRequest('chipset.reset', []);
  }

  async getRegisters(): Promise<RegisterBank> {
    return this.sendRequest('cpu.getRegisters', []) as Promise<RegisterBank>;
  }

  async getFrameBuffer(): Promise<Uint8Array> {
    return this.sendRequest('screen.getFrameBuffer', []) as Promise<Uint8Array>;
  }

  async setSpeed(speed: number): Promise<void> {
    await this.sendRequest('chipset.setSpeed', [speed]);
  }

  async sendInputEvent(event: InputEvent): Promise<void> {
    const entry = this._modelConfig?.keyMap.find(k => k.keyCode === event.keyCode);
    if (!entry || entry.row === undefined || entry.col === undefined) return;
    if (event.pressed) {
      await this.sendRequest('keyboard.keyPress', [entry.row, entry.col]);
    } else {
      await this.sendRequest('keyboard.keyRelease', [entry.row, entry.col]);
    }
  }

  async saveSnapshot(): Promise<ChipsetSnapshot> {
    return this.sendRequest('chipset.saveSnapshot', []) as Promise<ChipsetSnapshot>;
  }

  async loadSnapshot(snapshot: ChipsetSnapshot): Promise<void> {
    await this.sendRequest('chipset.loadSnapshot', [snapshot]);
  }

  async saveSnapshotSerialized(): Promise<ChipsetSnapshot> {
    return this.sendRequest('chipset.saveSnapshotSerialized', []) as Promise<ChipsetSnapshot>;
  }

  async loadSnapshotSerialized(snapshot: ChipsetSnapshot): Promise<void> {
    const serialized = serializeSnapshot(snapshot);
    const transferables = collectTransferables(serialized);
    await this.sendRequest('chipset.loadSnapshotSerialized', [serialized], transferables);
  }

  getMode(): 'worker' | 'inline' {
    return this._mode;
  }

  async disassemble(csr: number, address: number, count: number): Promise<DisassembledInstruction[]> {
    return this.sendRequest('debug.disassemble', [csr, address, count]) as Promise<DisassembledInstruction[]>;
  }

  async readMemory(address: number, length: number): Promise<Uint8Array> {
    return this.sendRequest('debug.readMemory', [address, length]) as Promise<Uint8Array>;
  }

  async writeMemory(address: number, data: Uint8Array): Promise<void> {
    await this.sendRequest('debug.writeMemory', [address, Array.from(data)]);
  }

  async addBreakpoint(bp: BreakpointData): Promise<void> {
    await this.sendRequest('debug.addBreakpoint', [bp]);
  }

  async removeBreakpoint(address: number, csr: number): Promise<void> {
    await this.sendRequest('debug.removeBreakpoint', [address, csr]);
  }

  async executeTerminal(command: string): Promise<string> {
    return this.sendRequest('terminal.execute', [command]) as Promise<string>;
  }

  getLatestState(): EmulatorState | null {
    return this.latestState;
  }

  destroy(): void {
    if (this.worker) {
      for (const [, pending] of this.pendingRequests) {
        clearTimeout(pending.timer);
        pending.reject(new Error('Worker destroyed'));
      }
      this.pendingRequests.clear();
      this.worker.terminate();
      this.worker = null;
      this.latestState = null;
    }
  }

  restart(): void {
    this.destroy();
    if (this.workerFactory) {
      this.worker = this.workerFactory();
    } else {
      throw new Error('Cannot restart: no worker factory available');
    }
    this.worker.onmessage = (e: MessageEvent<WorkerMessage>) => this.onMessage(e);
    this.worker.onerror = (e: ErrorEvent) => {
      if (this.onError) {
        this.onError(new Error(e.message || 'Worker error'));
      }
    };
  }
}
